import {
	Combobox,
	ComboboxContent,
	ComboboxEmpty,
	ComboboxInput,
	ComboboxItem,
	ComboboxList,
	ComboboxTrigger,
	ComboboxValue,
	DateRangePicker,
	FILTER_CONTROL_HEIGHT_CLASS,
	FilterBar,
	FilterClearButton,
	FilterField,
	Spinner,
	useComboboxAnchor,
} from "@components";
import { styles as buttonStyles } from "@components/button/button-untitledui";
import { type Dispatch, type SetStateAction, useState } from "react";
import { cx } from "@/utils/cx";
import type { VehiclesLookupSelectItem } from "../handlers";
import {
	type ExpenseReportDateRangeValue,
	useExpenseReportDateRange,
	useGetVehiclesLookup,
} from "../hooks";

function normalizePlateForSearch(value: string): string {
	return value.replace(/[^a-z0-9]/gi, "").toUpperCase();
}

type ExpensesReportsFiltersProps = {
	defaultDateRange: ExpenseReportDateRangeValue;
	onDateRangeChange: (value: ExpenseReportDateRangeValue) => void;
	onSelectedVehiclesChange: Dispatch<SetStateAction<string[]>>;
};

export function ExpensesReportsFilters({
	defaultDateRange,
	onDateRangeChange,
	onSelectedVehiclesChange,
}: ExpensesReportsFiltersProps) {
	const anchor = useComboboxAnchor();

	const [selectedVehicles, setSelectedVehicles] = useState<
		VehiclesLookupSelectItem[]
	>([]);
	const { value, isDateUnavailable, handleChange, maxValue, minValue } =
		useExpenseReportDateRange(defaultDateRange);

	const { data: vehiclesData, isLoading: isLoadingVehicles } =
		useGetVehiclesLookup();

	return (
		<FilterBar
			actions={
				selectedVehicles.length > 0 ? (
					<FilterClearButton
						onClick={() => {
							setSelectedVehicles([]);
							onSelectedVehiclesChange([]);
						}}
					/>
				) : null
			}
			label="Filtros dos relatórios de despesas"
		>
			<FilterField label="Período">
				<DateRangePicker
					aria-label="Período do relatório"
					buttonClassName="w-full"
					className="w-full"
					isDateUnavailable={isDateUnavailable}
					maxValue={maxValue}
					minValue={minValue}
					onApply={(range) => {
						onDateRangeChange(range);
					}}
					onChange={handleChange}
					shouldCloseOnSelect={false}
					size="sm"
					value={value}
				/>
			</FilterField>

			<FilterField className="sm:w-72" label="Veículos">
				<Combobox
					autoHighlight
					filter={(itemValue: VehiclesLookupSelectItem, query: string) => {
						const normalizedQuery = normalizePlateForSearch(query);

						// Se a busca estiver vazia, mantém todos os itens visíveis.
						if (!normalizedQuery) {
							return true;
						}

						// A busca deve ignorar qualquer caractere que não seja
						// letra ou número (ex: "-", ".", espaço, etc).
						const normalizedItemPlate = normalizePlateForSearch(
							itemValue.label
						);

						return normalizedItemPlate.includes(normalizedQuery);
					}}
					items={vehiclesData}
					itemToStringValue={(vehicle: VehiclesLookupSelectItem) => vehicle.id}
					multiple
					onValueChange={(fieldValue) => {
						setSelectedVehicles(fieldValue);
						onSelectedVehiclesChange(fieldValue.map((vehicle) => vehicle.id));
					}}
					value={selectedVehicles}
				>
					<ComboboxTrigger
						render={
							<button
								aria-label="Filtrar por veículos"
								className={cx(
									buttonStyles.common.root,
									buttonStyles.sizes.sm.root,
									buttonStyles.colors.secondary.root,
									`${FILTER_CONTROL_HEIGHT_CLASS} w-full justify-start px-3.5 font-normal text-secondary text-sm`
								)}
								disabled={isLoadingVehicles}
								type="button"
							>
								{!!isLoadingVehicles && <Spinner disabled />}
								<span className="w-full max-w-full truncate">
									<ComboboxValue>
										{(cbxState: VehiclesLookupSelectItem[]) => {
											const formattedValue = (
												values: VehiclesLookupSelectItem[]
											) => {
												if (values.length === 0) {
													return "Todos os veículos";
												}
												if (values.length === 1) {
													return values[0].label ?? "";
												}
												return `${values.length} veículos`;
											};
											return formattedValue(cbxState);
										}}
									</ComboboxValue>
								</span>
							</button>
						}
					/>
					<ComboboxContent anchor={anchor} className="w-72 bg-primary">
						<ComboboxInput
							placeholder="Busca por veículo"
							showTrigger={false}
						/>
						<ComboboxEmpty>Nenhum veículo encontrado.</ComboboxEmpty>
						<ComboboxList>
							{(item) => (
								<ComboboxItem
									className="h-10 min-h-fit text-md"
									key={item.id}
									value={item}
								>
									{item.label}
								</ComboboxItem>
							)}
						</ComboboxList>
					</ComboboxContent>
				</Combobox>
			</FilterField>
		</FilterBar>
	);
}
